<script setup lang="ts">
import { reactive, ref, watch } from "vue";
import AuthPage from "~/components/templates/AuthPage.vue";
import AuthCard from "~/components/templates/AuthCard.vue";

const { $api } = useNuxtApp();
const authStore = useAuthStore();
const { formErrors, errorMsg, setErrors, clearErrors } = useResponseError();
const { t } = useI18n();
const route = useRoute();

const form = reactive({ email: "", password: "" });
const loading = ref(false);
const showPassword = ref(false);

async function submit() {
  loading.value = true;
  const timestamp = Date.now();

  try {
    const loginResp = (await $api("/login", {
      method: "POST",
      body: form,
    })) as any;

    clearErrors();

    const profileResp = (await $api("/profile", {
      headers: {
        Authorization: `Bearer ${loginResp.token}`,
      },
    })) as any;

    authStore.login(loginResp.token, profileResp);

    const redirectPath = (route.query.redirect as string) || "/";
    navigateTo(redirectPath);
  } catch (err: any) {
    setErrors(err);
  } finally {
    const elapsed = Date.now() - timestamp;
    const timeout = elapsed > 400 ? 0 : 400 - elapsed;
    setTimeout(() => {
      loading.value = false;
    }, timeout);
  }
}

watch(
  () => form,
  () => {
    clearErrors();
  },
  { deep: true },
);
</script>

<template>
  <AuthPage>
    <AuthCard :title="t('login.title')">
      <form class="flex flex-col gap-medium" @submit.prevent="submit">
        <FormField
          v-model="form.email"
          type="email"
          :label="t('field.email')"
          :placeholder="t('login.emailPlaceholder')"
          :error="formErrors.email"
        />

        <FormField
          v-model="form.password"
          :type="showPassword ? 'text' : 'password'"
          :label="t('field.password')"
          :placeholder="t('login.passwordPlaceholder')"
          :error="formErrors.password"
          @keydown.enter="submit"
        />

        <InlineMessage v-if="errorMsg" variant="negative">
          {{ errorMsg }}
        </InlineMessage>

        <Button
          type="submit"
          size="medium"
          variant="primary"
          :disabled="loading"
          class="w-full"
        >
          {{ loading ? t("login.loggingIn") : t("login.login") }}
        </Button>
      </form>
    </AuthCard>
  </AuthPage>
</template>

<style scoped lang="scss"></style>
